/**
 * RecipeDetailPane (S4, §4.3 verbatim + CONSULTS §2) — the "What you'll get"
 * overlay detail pane. Opens OVER the library grid (the grid stays a grid
 * behind it — no list collapse, no scroll loss).
 *
 * Contents:
 * - Header: name, class label (from the SERVED profile_class via the
 *   constants module), YOURS chip when tenant-owned, always-visible close.
 * - WHAT YOU'LL GET: the Shape (S2 full variant) + honesty caption (the
 *   variable-output feedforward line, at the point of action).
 * - Fixed-slot baseline glyph row (CONSULTS §2): identical slot order on
 *   every recipe; present = full contrast; absent-by-design = struck with
 *   em-dash at low contrast — NEVER plain grey. Optional secondary count
 *   beside the row, never replacing it.
 * - Label–value groups (Length & tone / Structure / Components / Links):
 *   floor-clamped values; baseline-aligned pairs, whitespace separators.
 * - observed_shape annotation (A2): render-gated n>=5 with window label,
 *   visually distinct from config-derived values. The backend does NOT
 *   serve the field yet — the gate stays dormant; nothing is fabricated
 *   and there is no placeholder UI.
 *
 * Presentation seam: props-only + pure derivations; edit content is passed
 * in as a node (S5 replaces it with the Edit focus state).
 */

import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Rocket, Copy, Trash2, Pencil, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BlogTemplateSummary } from '@/services/blogTemplatesService';
import { defaultConfigToRecipe } from './recipePayload';
import { canShowObserved, parseShapeFloor } from './shape/shapeFloor';
import { clampRecipeToFloor } from './shape/clampToFloor';
import { RecipeShape } from './shape/RecipeShape';
import { GlyphSlotRow } from './shape/GlyphSlotRow';
import { classLabelFor } from './shape/galleryClass';
import { GALLERY_STRINGS } from './galleryStrings';

export interface RecipeDetailPaneProps {
  template: BlogTemplateSummary;
  onClose: () => void;
  onUse: (template: BlogTemplateSummary) => void;
  /** W1 — "Expand shape" slot: opens the TemplatePreviewDrawer for this
   *  template. Optional so drawer-less hosts keep compiling. */
  onExpandShape?: (template: BlogTemplateSummary) => void;
  onCustomize: (template: BlogTemplateSummary) => void;
  onDelete: (template: BlogTemplateSummary) => void;
  /** Delete affordance gate — computed by the caller (presentation seam). */
  canDelete: boolean;
  /** Interim edit mode (S5 replaces with the Edit focus state): when set,
   *  this node replaces the view content below the header. */
  isEditing?: boolean;
  onToggleEdit?: () => void;
  editContent?: React.ReactNode;
  className?: string;
}

const LabelValue: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-baseline gap-2">
    <span className="text-xs text-muted-foreground w-16 flex-shrink-0">{label}</span>
    <span className="text-sm tabular-nums" data-label-value="true">{value}</span>
  </div>
);

export const RecipeDetailPane: React.FC<RecipeDetailPaneProps> = ({
  template,
  onClose,
  onUse,
  onExpandShape,
  onCustomize,
  onDelete,
  canDelete,
  isEditing = false,
  onToggleEdit,
  editContent,
  className,
}) => {
  const recipe = useMemo(
    () => defaultConfigToRecipe(template.default_config),
    [template.default_config],
  );
  const floor = useMemo(
    () => parseShapeFloor(template.shape_floor),
    [template.shape_floor],
  );
  const clamped = useMemo(() => clampRecipeToFloor(recipe, floor), [recipe, floor]);
  const observed = template.observed_shape;
  const showObserved = canShowObserved(observed);

  const isYours = !template.is_system;
  const wordsText = `~${clamped.words.value.toLocaleString('en-US')}`;
  const sectionsText =
    clamped.minSections.value === clamped.maxSections.value
      ? `${clamped.minSections.value}`
      : `${clamped.minSections.value}–${clamped.maxSections.value}`;
  const yesNo = (b: boolean) => (b ? 'yes' : 'no');
  const internalLinks =
    recipe.internalLinkCount != null ? `${recipe.internalLinkCount}` : GALLERY_STRINGS.pane.aiDecides;
  const externalLinks =
    recipe.externalLinkCount != null ? `${recipe.externalLinkCount}` : GALLERY_STRINGS.pane.aiDecides;

  return (
    <div
      data-testid="recipe-detail-pane"
      role="dialog"
      aria-label={`${template.name} — ${GALLERY_STRINGS.pane.whatYoullGet}`}
      className={cn(
        'fixed inset-y-0 right-0 z-40 w-full max-w-md flex flex-col border-l bg-card shadow-xl',
        className,
      )}
    >
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-lg truncate">{template.name}</h3>
              <span
                data-class-label="true"
                className="text-xs text-muted-foreground uppercase tracking-wide"
              >
                {classLabelFor(floor?.profile_class)}
              </span>
              {isYours && (
                <Badge
                  variant="secondary"
                  className="bg-teal-light text-teal-dark hover:bg-teal-light text-[10px] px-1.5 py-0"
                >
                  {GALLERY_STRINGS.card.yours}
                </Badge>
              )}
            </div>
            {template.description && (
              <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                {template.description}
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label={GALLERY_STRINGS.pane.close}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Body */}
      <ScrollArea className="flex-1">
        {isEditing && editContent ? (
          <div className="p-4">{editContent}</div>
        ) : (
          <div className="p-4 flex flex-col gap-5">
            {/* WHAT YOU'LL GET — Shape + honesty caption */}
            <section data-testid="what-youll-get">
              <h4 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase mb-2">
                {GALLERY_STRINGS.pane.whatYoullGet}
              </h4>
              <div className="flex gap-4">
                <RecipeShape recipe={recipe} floor={floor} variant="full" className="w-32 flex-shrink-0" />
                <p data-honesty-caption="true" className="text-xs text-muted-foreground max-w-[180px]">
                  {GALLERY_STRINGS.pane.honestyCaption}
                </p>
              </div>
            </section>

            {/* Fixed-slot baseline glyph row (CONSULTS §2) */}
            <GlyphSlotRow glyphs={clamped.glyphs} />

            {/* A2 observed-shape annotation — dormant until the backend
                serves observed_shape (n>=5 gate; never fabricated). */}
            {showObserved && (
              <p data-observed-shape="true" className="text-xs italic text-ink-2">
                {observed.median_sections != null && `typically ${observed.median_sections} sections`}
                {observed.median_sections != null && observed.median_words != null && ', '}
                {observed.median_words != null && `~${observed.median_words.toLocaleString('en-US')} words`}
                {` — ${observed.window_label}`}
              </p>
            )}

            {/* W1 — "Expand shape" slot: END of the "What you'll get" zone,
                directly beneath the Shape + GlyphSlotRow, ABOVE the
                settings-groups hairline. Small quiet button (never a text
                link, never icon-only); always visible, never hover-gated.
                Footer action row is untouched — Use stays sole coral. */}
            {onExpandShape && (
              <button
                type="button"
                data-expand-shape="true"
                onClick={() => onExpandShape(template)}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-warm-border-strong bg-paper px-3 py-2 text-sm font-medium text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink"
              >
                {GALLERY_STRINGS.pane.expandShape}
                <ChevronRight className="h-4 w-4" />
              </button>
            )}

            {/* Label–value groups */}
            <div className="flex flex-col gap-4 border-t pt-4">
              <section data-group="length-tone">
                <h5 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase mb-1.5">
                  {GALLERY_STRINGS.pane.groups.lengthTone}
                </h5>
                <LabelValue label="Words" value={wordsText} />
                <LabelValue label="Tone" value={recipe.tone} />
              </section>
              <section data-group="structure">
                <h5 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase mb-1.5">
                  {GALLERY_STRINGS.pane.groups.structure}
                </h5>
                <LabelValue label="Sections" value={sectionsText} />
                <LabelValue label="TOC" value={yesNo(clamped.glyphs.toc)} />
              </section>
              <section data-group="components">
                <h5 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase mb-1.5">
                  {GALLERY_STRINGS.pane.groups.components}
                </h5>
                <LabelValue label="FAQ" value={`${clamped.faqItems.value}`} />
                <LabelValue label="Images" value={`${clamped.images.value}`} />
                <LabelValue label="Callouts" value={`${clamped.callouts.value}`} />
              </section>
              <section data-group="links">
                <h5 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase mb-1.5">
                  {GALLERY_STRINGS.pane.groups.links}
                </h5>
                <LabelValue label="Internal" value={internalLinks} />
                <LabelValue label="External" value={externalLinks} />
              </section>
            </div>
          </div>
        )}
      </ScrollArea>

      {/* Actions */}
      <div className="flex-shrink-0 p-4 border-t flex flex-col gap-2">
        <Button
          onClick={() => onUse(template)}
          className="bg-coral hover:bg-coral-hover"
        >
          <Rocket className="h-4 w-4 mr-2" />
          {GALLERY_STRINGS.pane.useThisTemplate}
        </Button>
        <div className="flex gap-2">
          {isYours && onToggleEdit && (
            <Button variant="outline" className="flex-1" onClick={onToggleEdit}>
              <Pencil className="h-4 w-4 mr-2" />
              {GALLERY_STRINGS.pane.edit}
            </Button>
          )}
          <Button variant="outline" className="flex-1" onClick={() => onCustomize(template)}>
            <Copy className="h-4 w-4 mr-2" />
            {isYours ? GALLERY_STRINGS.pane.duplicate : GALLERY_STRINGS.pane.makeYourOwnCopy}
          </Button>
          {canDelete && (
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(template)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
